import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose, IoMdSearch } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { ApiPost } from "../../helper/axiosFunctions";
import { commonToast } from "../../helper/Toast";
import Loading from "../Loading";
import { useLocation } from "react-router-dom";
import SubjectCard from "../SubjectCard";
import SubjectFilter from "../SubjectFilter";
import PopularityFIlter from "../PopularityFIlter";

export default function ChooseSubject({
  isCreatemodel,
  setIsCreatemodel,
  setIsDetail,
  isDetail,
  addTask,
  fetchDecks
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExpand, setIsExpand] = useState(false);
  const [lastId, setLastId] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [filter, setFilter] = useState([
    {
      title: t("filter.popularity"),
      isActive: true,
    },
    {
      title: t("filter.newest"),
      isActive: false,
    },
    {
      title: t("filter.recently_watched"),
      isActive: false,
    },
  ]);
  const observer = useRef(null);
  const isFirstRender = useRef(true);
  const classId = pathname?.split('/')[2];
  const [loadingDeckId, setLoadingDeckId] = useState(null);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
  };

  const handleFilterClick = (index) => {
    setFilter(prevFilter => 
      prevFilter.map((item, i) => ({
        ...item,
        isActive: i === index
      }))
    );
  };

  const GetDeckData = async (reset = false) => {
    if (!hasMore && !reset) return;
    setLoading(true);
    try {
      const url = reset || !lastId
        ? `/contents/decks/search?pageSize=8`
        : `/contents/decks/search?lastId=${lastId}&pageSize=8`;

      const requestBody = {
        deckName: search || "",
        countries:["LT"],
        "status":  "PUBLISHED"  
      };

      // Add subject filter if selected
      if (selectedSubject) {
        requestBody.mainSubject = selectedSubject;
      }

      const response = await ApiPost(url, requestBody);

      // Filter out deleted decks (handle different possible status values)
      const newDecks = (response?.data || []).filter(deck => {
        const status = deck?.status?.toUpperCase();
        // List of statuses that should be hidden
        const hiddenStatuses = ["DELETED", "INACTIVE", "REMOVED", "ARCHIVED", "DISABLED", "HIDDEN"];
        return !hiddenStatuses.includes(status);
      });

      if (reset) {
        setData(newDecks);
        setLastId(newDecks.length > 0 ? newDecks[newDecks.length - 1]._id : "");
        setHasMore(newDecks.length > 0);
      } else {
        if (newDecks.length === 0) {
          setHasMore(false);
        } else {
          setData((prevData) => [...prevData, ...newDecks]);
          setLastId(newDecks[newDecks.length - 1]._id);
        }
      }
    } catch (error) {
      commonToast(error?.data?.error || error?.message, "error");
    } finally {
      setLoading(false);
    }
  };

  //for search filter and subject filter
  useEffect(() => {
    window.scrollTo(0, 0);
    if (isFirstRender.current && search.length === 0 && !selectedSubject) {
      isFirstRender.current = false;
      return;
    }
    const delayDebounce = setTimeout(() => {
      GetDeckData(true);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [search, selectedSubject]);

  //for pagination
  useEffect(() => {
    if (loading || !hasMore) return;
    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          GetDeckData();
        }
      },
      { threshold: 1.0 }
    );

    if (observer.current && document.getElementById("loadMoreTrigger")) {
      observer.current.observe(document.getElementById("loadMoreTrigger"));
    }

    return () => observer.current?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore]);

  // Add this new effect for initial data fetch
  useEffect(() => {
    if (isCreatemodel) {
      // Reset data when modal opens to ensure fresh data
      setData([]);
      setLastId("");
      setHasMore(true);
      GetDeckData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatemodel]);

  // Add effect to control body overflow
  useEffect(() => {
    if (isCreatemodel) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isCreatemodel]);

  const handleDeckClick = async (deck) => {
    setLoadingDeckId(deck.deckId);
    try {
      const response = await ApiPost(`/contents/classes/${classId}/decks/${deck.deckId}`);
      if (response?.data) {
        fetchDecks();
        addTask(response.data);
        setIsCreatemodel(false);
      }
    } catch (error) {
      commonToast(error?.data?.errorDescription || error?.data?.error, "error");
    } finally {
      setLoadingDeckId(null);
    }
  };

  return (
    <div>
      <Dialog
        open={isCreatemodel}
        as="div"
        className="relative z-30 focus:outline-none"
        onClose={() => setIsCreatemodel(true)}
      >
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center py-8 px-4 bg-black/35 backdrop-blur-sm">
            <DialogPanel
              transition
              className="w-full relative max-w-6xl max-h-[90vh] rounded-3xl bg-white duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0"
            >
              <div className="p-6 bg-white relative z-10 rounded-3xl flex flex-col" style={{ height: 'calc(90vh - 4rem)' }}>
                <div className="h-14 border-b flex overflow-auto justify-between items-center text-xl font-semibold">
                  <ul className="flex items-center gap-5 text-base h-full text-lightgray font-semibold">
                    <li className="border-b border-black text-black h-full flex items-center justify-center cursor-pointer">
                      {t("catalog")}
                    </li>
                    <li className="h-full flex items-center justify-center cursor-pointer">
                      {t("Favorites")}
                    </li>
                    <li className="h-full flex items-center justify-center cursor-pointer">
                      {t("useit")}
                    </li>
                  </ul>
                  <IoMdClose
                    className="absolute top-7 right-4 cursor-pointer"
                    fontSize={20}
                    onClick={() => {
                      setIsCreatemodel(false);
                    }}
                  />
                </div>

                <div className="flex flex-wrap justify-between items-center my-3">
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <div
                      className="h-10 relative border cursor-pointer rounded-full flex items-center transition-width duration-300"
                      onClick={() => setIsExpand(true)}
                    >
                      <IoMdSearch size={24} className="absolute left-2" />
                      <input
                        type="text"
                        value={search}
                        onChange={handleSearch}
                        className={`${isExpand ? "w-full" : "w-10"
                          } transition-all duration-100 ease-out h-10 border rounded-full ps-10`}
                      />
                    </div>
                    <div className="border rounded-full h-10 px-2 flex justify-center items-center">
                      {t("Alldisciplines")}
                    </div>
                    <div className="border rounded-full h-10 flex justify-center items-center px-3">
                      {t("allages")}
                    </div>
                    <div className="border rounded-full h-10 flex justify-center items-center px-3">
                      {t("Allauthors")}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="border rounded-full h-10 flex justify-center items-center md:mb-4">
                      <SubjectFilter onSubjectChange={handleSubjectChange} />
                    </div>
                    <div className="border rounded-full h-10 flex justify-center items-center">
                      <PopularityFIlter
                        filter={filter}
                        setFilter={setFilter}
                        handleFilterClick={handleFilterClick}
                      />
                    </div>
                  </div>
                </div>

                {/* Scrollable Content Area */}
                <div className="flex-1 overflow-y-auto min-h-0 py-4">
                  {/* Data Display Section */}
                  {data.length > 0 ? (
                    <SubjectCard 
                      data={data}
                      onCardClick={handleDeckClick}
                      isModal={true}
                      loadingCardId={loadingDeckId}
                      showAddIcon={true}
                    />
                  ) : null}

                  {/* Loading State */}
                  {loading && (
                    <div className="flex justify-center items-center py-10">
                      <Loading />
                    </div>
                  )}

                  {/* No Data State */}
                  {!loading && data?.length === 0 && (
                    <div className="text-center text-gray-500 py-10">
                      <p>No decks found.</p>
                    </div>
                  )}

                  {/* Load More Trigger */}
                  {hasMore && !loading && (
                    <div id="loadMoreTrigger" className="h-1"></div>
                  )}
                </div>

              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
